import React from 'react';
import { Dropdown, Tooltip } from 'antd';
import {
  AudioMutedOutlined,
  AudioOutlined,
  CloseOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  SoundOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { colors } from '../../theme';
import { VOICE_PHASE, isVoiceLive } from './useVoiceSession';
import { voiceErrorMessage } from './voiceErrors';
import { STT_PROVIDER, sttOptionsForProviders, sttProviderLabel } from './voiceStt';

const PHASE_LABEL = {
  [VOICE_PHASE.IDLE]: 'Start voice',
  [VOICE_PHASE.CREATING_SESSION]: 'Connecting',
  [VOICE_PHASE.CONNECTING]: 'Connecting',
  [VOICE_PHASE.CONNECTED]: 'Connected',
  [VOICE_PHASE.LISTENING]: 'Listening',
  [VOICE_PHASE.SPEAKING]: 'Speaking',
  [VOICE_PHASE.PROCESSING]: 'Processing',
  [VOICE_PHASE.ASSISTANT_SPEAKING]: 'Assistant speaking',
  [VOICE_PHASE.PAUSED]: 'Paused',
  [VOICE_PHASE.STOPPING]: 'Stopping',
};

const srOnly = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export const voicePhaseLabel = (phase) => PHASE_LABEL[phase] || PHASE_LABEL[VOICE_PHASE.IDLE];

export default function VoiceControl({
  phase,
  errorCode,
  audioBlocked,
  muted = false,
  autoMuted = false,
  sttProvider = STT_PROVIDER.PIPECAT,
  sttProviders,
  onSttProviderChange,
  onStart,
  onStop,
  onResumeAudio,
  onClearError,
  onToggleMute,
  disabled = false,
  pillStyle,
  activeStyle,
}) {
  const idle = phase === VOICE_PHASE.IDLE;
  const live = isVoiceLive(phase);
  const heldForAnswer = autoMuted && !muted;
  const micOff = muted || autoMuted;
  const busy = phase === VOICE_PHASE.CREATING_SESSION || phase === VOICE_PHASE.CONNECTING || phase === VOICE_PHASE.STOPPING;
  const label = voicePhaseLabel(phase);
  const sttOptions = sttOptionsForProviders(sttProviders);
  const showSttPicker = idle && typeof onSttProviderChange === 'function' && sttOptions.length > 0;
  const actionLabel = idle ? 'Start voice' : `Stop voice (${label})`;
  const tooltip = idle
    ? 'Talk to the assistant with your microphone. Answers come from this project. Memory is turned on so follow-up sentences keep context. While it answers you can say "hold on", "continue", "repeat" or "stop".'
    : phase === VOICE_PHASE.STOPPING
      ? 'Stopping voice…'
      : phase === VOICE_PHASE.PAUSED
        ? 'Answer paused. Say "continue" to pick up where it stopped or "stop" to discard it. Click or press Escape to stop voice.'
        : `Voice is ${label.toLowerCase()}. Click or press Escape to stop voice.`;

  const icon = busy ? (
    <LoadingOutlined spin style={{ fontSize: 12 }} aria-hidden />
  ) : idle ? (
    <AudioOutlined style={{ fontSize: 12 }} aria-hidden />
  ) : (
    <StopOutlined style={{ fontSize: 12 }} aria-hidden />
  );

  const handleClick = () => {
    if (idle) onStart?.();
    else onStop?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !idle) {
      e.preventDefault();
      onStop?.();
    }
  };

  return (
    <>
      {showSttPicker ? (
        <Tooltip title="Speech recognition engine used while you talk. Change this before starting voice.">
          <span>
            <Dropdown
              disabled={disabled}
              menu={{
                selectable: true,
                selectedKeys: [sttProvider],
                onClick: ({ key }) => onSttProviderChange?.(key),
                items: sttOptions.map((opt) => ({ key: opt.value, label: opt.label })),
              }}
              trigger={['click']}
            >
              <button
                type="button"
                disabled={disabled}
                aria-label={`Speech engine: ${sttProviderLabel(sttProvider)}`}
                data-voice-stt={sttProvider}
                style={{
                  ...pillStyle,
                  ...(disabled ? { cursor: 'not-allowed', opacity: 0.6 } : null),
                }}
              >
                <span>{sttProviderLabel(sttProvider)}</span>
                <DownOutlined style={{ fontSize: 10, opacity: 0.7 }} aria-hidden />
              </button>
            </Dropdown>
          </span>
        </Tooltip>
      ) : null}
      <Tooltip title={tooltip}>
        <button
          type="button"
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          disabled={(idle && disabled) || phase === VOICE_PHASE.STOPPING}
          aria-label={actionLabel}
          aria-pressed={!idle}
          data-voice-phase={phase}
          style={{
            ...pillStyle,
            ...(idle ? null : activeStyle),
            ...((idle && disabled) || phase === VOICE_PHASE.STOPPING ? { cursor: 'not-allowed', opacity: 0.6 } : null),
          }}
        >
          {icon}
          <span>{label}</span>
        </button>
      </Tooltip>
      {!idle && onToggleMute ? (
        <Tooltip
          title={
            heldForAnswer
              ? 'Your question has been sent. The microphone is off until the answer starts, so extra speech is not added to the question.'
              : muted
                ? 'Microphone muted. Nothing you say is heard or sent. Click to unmute.'
                : 'Mute your microphone. The assistant keeps talking but will not hear you.'
          }
        >
          <button
            type="button"
            onClick={() => onToggleMute(!muted)}
            disabled={!live || heldForAnswer}
            aria-label={heldForAnswer ? 'Microphone off while the answer is prepared' : muted ? 'Unmute microphone' : 'Mute microphone'}
            aria-pressed={micOff}
            data-voice-muted={muted ? 'true' : 'false'}
            data-voice-auto-muted={heldForAnswer ? 'true' : 'false'}
            style={{
              ...pillStyle,
              ...(micOff ? activeStyle : null),
              ...(!live || heldForAnswer ? { cursor: 'not-allowed', opacity: 0.6 } : null),
            }}
          >
            {micOff ? (
              <AudioMutedOutlined style={{ fontSize: 12 }} aria-hidden />
            ) : (
              <AudioOutlined style={{ fontSize: 12 }} aria-hidden />
            )}
            <span>{heldForAnswer ? 'Mic off' : muted ? 'Unmute' : 'Mute'}</span>
          </button>
        </Tooltip>
      ) : null}
      {audioBlocked ? (
        <Tooltip title="Your browser blocked automatic playback. Click to hear the assistant.">
          <button
            type="button"
            onClick={onResumeAudio}
            aria-label="Enable assistant audio"
            style={pillStyle}
          >
            <SoundOutlined style={{ fontSize: 12 }} aria-hidden />
            <span>Enable audio</span>
          </button>
        </Tooltip>
      ) : null}
      {errorCode ? (
        <span
          role="alert"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            lineHeight: 1.3,
            color: colors.error,
            maxWidth: 360,
          }}
        >
          <ExclamationCircleOutlined aria-hidden />
          <span>{voiceErrorMessage(errorCode)}</span>
          <button
            type="button"
            onClick={onClearError}
            aria-label="Dismiss voice error"
            style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0 }}
          >
            <CloseOutlined style={{ fontSize: 10 }} aria-hidden />
          </button>
        </span>
      ) : null}
      <span role="status" aria-live="polite" style={srOnly}>
        {idle ? '' : `Voice ${label.toLowerCase()}${muted ? ', microphone muted' : ''}`}
      </span>
    </>
  );
}
